/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { Promise } from 'rsvp';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';

import { task } from 'ember-concurrency';
import { FlyActionDropdownItem } from '@customerio/fly-components/addon/components/fly-action-dropdown';

import AnalyticsService from 'cio/services/analytics';
import CampaignModel, { CampaignStateActions } from 'cio/models/campaign';
import NewsletterModel from 'cio/models/newsletter';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import RouterService from '@ember/routing/router-service';
import ActionSubjectsService from 'cio/services/action-subjects';
import AuthService from 'cio/services/auth';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import ActionModel from 'cio/models/action';
import { capitalize } from '@ember/string';

type CampaignBroadcastNewsletterActionsArgsBase = {
  showDropdown?: boolean;
  triggerLabel?: string;
  afterArchiveAction?: () => unknown;
  afterDeleteAction?: () => unknown;
  afterUnarchiveAction?: () => unknown;
  afterCopyAction?: () => unknown;
};

type CampaignBroadcastNewsletterActionsArgsCampaign = CampaignBroadcastNewsletterActionsArgsBase & {
  model: CampaignModel;
  campaignHasUnsentDrafts: boolean;
};

type CampaignBroadcastNewsletterActionsArgsNewsletter = CampaignBroadcastNewsletterActionsArgsBase & {
  model: NewsletterModel;
};

type ItemActionOptions = {
  dropdownItems: FlyActionDropdownItem[];
  buttonItems: FlyActionDropdownItem[];
};

type EnvData = {
  id: string | undefined;
  name: string | undefined;
};

export enum ModalStates {
  Closed = 'closed',
  Archive = 'archive',
  Unarchive = 'unarchive',
  Restart = 'restart',
  Delete = 'delete',
  CopyToEnv = 'copyToEnv',
  Stop = 'stop',
}

enum NewsletterCopyLabels {
  Duplicate = 'Duplicate',
  CopyTo = 'Copy to',
}

export default class CampaignBroadcastNewsletterActions extends Component<
  CampaignBroadcastNewsletterActionsArgsCampaign | CampaignBroadcastNewsletterActionsArgsNewsletter
> {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service actionSubjects!: ActionSubjectsService;
  @service auth!: AuthService;
  @service router!: RouterService;

  // for template usage:
  CampaignStateActions = CampaignStateActions;
  ModalStates = ModalStates;

  // internal
  @tracked modal: ModalStates = ModalStates.Closed;
  @tracked stopModalSelectedAction: CampaignStateActions.Stop | CampaignStateActions.Sunset | null = null;
  @tracked restartModalBackfillSetting: boolean | null = null;
  @tracked campaignActiveSubjectsCount: number | null = 0;

  get typeName(): 'broadcast' | 'campaign' | 'newsletter' {
    if (this.args.model instanceof CampaignModel) {
      return this.args.model.isApiTriggered ? 'broadcast' : 'campaign';
    }

    return 'newsletter';
  }

  get hasReadOnlyPermission() {
    if (this.typeName === 'broadcast') {
      return (
        !this.auth.hasPermission('journeys/broadcasts/edit') || !this.auth.hasPermission('journeys/broadcasts/delete')
      );
    }

    if (this.typeName === 'newsletter') {
      return (
        !this.auth.hasPermission('journeys/newsletters/edit') || !this.auth.hasPermission('journeys/newsletters/delete')
      );
    }

    if (this.typeName === 'campaign') {
      return (
        !this.auth.hasPermission('journeys/campaigns/edit') || !this.auth.hasPermission('journeys/campaigns/delete')
      );
    }

    return true;
  }

  get hasInAppMessages(): boolean {
    if (this.args.model instanceof NewsletterModel) {
      return this.args.model.channel === 'in_app';
    }

    this.args.model.ensureParsedTree();

    return this.args.model.consolidatedActions.some((action: ActionModel) => {
      return action.type === 'in_app_action';
    });
  }

  get isCampaignBroadcastLive(): boolean {
    if (this.args.model instanceof NewsletterModel) {
      return false;
    }

    return this.args.model.isRunning || this.args.model.isSunsetting;
  }

  get showDropdown(): boolean {
    return this.args.showDropdown ?? true;
  }

  get hasInvalidWorkflowSettings(): boolean {
    let { model: campaign } = this.args as CampaignBroadcastNewsletterActionsArgsCampaign;
    return !campaign.validWorkflowSettings || campaign.hasActionsUsingDeletedSubscriptionTopic;
  }

  get itemActionOptions(): ItemActionOptions {
    let items: ItemActionOptions = {
      dropdownItems: [],
      buttonItems: [],
    };
    let itemType = this.showDropdown ? items.dropdownItems : items.buttonItems;

    if (this.args.model instanceof NewsletterModel) {
      // only copying newsletters is currently implemented
      items.dropdownItems.push({
        label: NewsletterCopyLabels.Duplicate,
        icon: 'duplicate',
        action: this.startCopy,
      });
      items.dropdownItems.push({
        label: NewsletterCopyLabels.CopyTo,
        icon: 'copy-to',
        action: this.startCopyToEnv,
      });
    } else if (!this.args.model.isApiTriggered) {
      // campaign state actions:
      let { model: campaign } = this.args;

      if (campaign.isStateChangeBackfilling) {
        // campaign state change backfill in progress: disable all actions until it settles
        return items;
      }

      if (campaign.isStopped || campaign.isSunsetting) {
        let content = campaign.hasActionsUsingDeletedSubscriptionTopic
          ? 'Please make sure all messages are using subscription topics that exist'
          : 'Please select campaign audience first';
        itemType.push({
          label: 'Restart',
          icon: 'play',
          action: this.startRestart,
          disabled: this.hasInvalidWorkflowSettings,
          tooltip: {
            condition: this.hasInvalidWorkflowSettings,
            content,
            direction: 'left',
          },
        });
      }

      if (campaign.isArchived) {
        itemType.push({
          label: 'Unarchive',
          action: this.startUnarchive,
          icon: 'unarchive',
          disabled: false,
        });
      }

      if (campaign.isRunning || campaign.isSunsetting) {
        itemType.push({ label: 'Stop', action: this.startStop, icon: 'stop' });
      }

      if (!campaign.isDraft && !campaign.isArchived) {
        itemType.push({ label: 'Archive', action: this.startArchive, icon: 'archive' });
      }
    }

    // Deleting: for both campaigns and newsletters
    itemType.push({
      label: 'Delete',
      icon: 'close',
      classNames: 'text-error',
      action: this.startDelete,
    });

    return items;
  }

  private async setCampaignActiveSubjectsCount() {
    let { model: campaign } = this.args as CampaignBroadcastNewsletterActionsArgsCampaign;

    await this.actionSubjects.fetch.perform(campaign.id);

    this.campaignActiveSubjectsCount = campaign.workflowActionIds.reduce(
      (subjectsCount: number, actionId) => subjectsCount + (this.actionSubjects.find(actionId)?.count ?? 0),
      0,
    );
  }

  changeCampaignStateOrDeleteTask = task(
    async (action: Exclude<CampaignStateActions, CampaignStateActions.Start> | 'delete') => {
      let { model: campaign } = this.args as CampaignBroadcastNewsletterActionsArgsCampaign;

      let promise: Promise<unknown>;

      if (action === 'delete') {
        promise = campaign.destroyRecord();
      } else if (action === CampaignStateActions.Restart) {
        let params: { stateAction: CampaignStateActions.Restart; backfill?: boolean } = { stateAction: action };
        if (typeof this.restartModalBackfillSetting === 'boolean') {
          params.backfill = this.restartModalBackfillSetting as boolean;
        }
        promise = campaign.changeState(params);
      } else {
        promise = campaign.changeState({ stateAction: action });
      }

      this.apiErrors.attach(promise);

      let alertCopy = {
        restart: `${capitalize(this.typeName)} restarted`,
        archive: `${capitalize(this.typeName)} archived`,
        unarchive: `${capitalize(this.typeName)} moving to active`,
        delete: `${capitalize(this.typeName)} deleted`,
        stop: `${capitalize(this.typeName)} stopped`,
        sunset: `${capitalize(this.typeName)} stopping`,
      }[action];

      try {
        await promise.then(async () => {
          // Calling after-promise callbacks in a then, rather than after the
          // await, because we want to guarantee the after-promise callbacks fire.
          // For example, if we were to call a "delete", using "await" in a task
          // would risk the after-promise callbacks not being called, due to this
          // task being cancelled if the parent component gets destroyed itself
          // (as a consequence of the delete)
          this.alerts.success(`campaign.${action}.success`, alertCopy);

          if (action === CampaignStateActions.Archive && this.args.afterArchiveAction != null) {
            await this.args.afterArchiveAction();
          }

          if (action === 'delete' && this.args.afterDeleteAction != null) {
            await this.args.afterDeleteAction();
          }

          if (action === CampaignStateActions.Unarchive && this.args.afterUnarchiveAction != null) {
            await this.args.afterUnarchiveAction();
          }
        });
      } catch (e) {
        // Ignore promise failure
      }
    },
  );

  deleteTask = task(async () => {
    await this.changeCampaignStateOrDeleteTask.perform('delete');
    this.modal = ModalStates.Closed;
  });

  archiveTask = task(async () => {
    await this.changeCampaignStateOrDeleteTask.perform(CampaignStateActions.Archive);
    this.modal = ModalStates.Closed;
  });

  restartTask = task(async () => {
    await this.changeCampaignStateOrDeleteTask.perform(CampaignStateActions.Restart);
    this.modal = ModalStates.Closed;
    this.restartModalBackfillSetting = null;
  });

  unarchiveTask = task(async () => {
    await this.changeCampaignStateOrDeleteTask.perform(CampaignStateActions.Unarchive);
    this.modal = ModalStates.Closed;
  });

  stopTask = task(async () => {
    if (!this.stopModalSelectedAction) {
      // "if"/return for type-checking - will never happen IRL
      return;
    }

    await this.changeCampaignStateOrDeleteTask.perform(this.stopModalSelectedAction);
    this.modal = ModalStates.Closed;
    this.stopModalSelectedAction = null;
  });

  copyTask = task(async (env: EnvData) => {
    try {
      // only newsletters can be copied:
      await this.onNewsletterCopy(this.args.model as NewsletterModel, env);
    } catch (e) {
      // Ignore promise failure
    } finally {
      this.modal = ModalStates.Closed;
    }
  });

  @action
  startDelete() {
    this.modal = ModalStates.Delete;
    this.analytics.track(this.args.model instanceof CampaignModel ? 'campaign' : 'broadcast', 'delete_started', {
      model: this.args.model,
    });
  }

  @action
  startStop() {
    let { campaignHasUnsentDrafts: hasUnsentDrafts, model: campaign } = this
      .args as CampaignBroadcastNewsletterActionsArgsCampaign;

    return this.setCampaignActiveSubjectsCount().then(() => {
      this.analytics.track('campaign', 'stop_started', {
        model: campaign,
        hasUnsentDrafts,
        hasJourneysInProgress: Boolean(this.campaignActiveSubjectsCount),
      });

      this.modal = ModalStates.Stop;

      // no active campaign journeys: hide "stop"/"stopping" modal controls, pre-select "stop" action
      if (!this.campaignActiveSubjectsCount) {
        this.stopModalSelectedAction = CampaignStateActions.Stop;
      }
    });
  }

  @action
  startArchive() {
    let { campaignHasUnsentDrafts: hasUnsentDrafts, model: campaign } = this
      .args as CampaignBroadcastNewsletterActionsArgsCampaign;

    // if already stopped: skip confirmation, campaign is doing nothing already
    if (campaign.isStopped) {
      this.analytics.track('campaign', 'archive_started', {
        model: campaign,
        hasUnsentDrafts,

        // already stopped: always no active journeys
        hasJourneysInProgress: false,
      });
      return this.archiveTask.perform();
    }

    return this.setCampaignActiveSubjectsCount().then(() => {
      this.analytics.track('campaign', 'archive_started', {
        model: campaign,
        hasUnsentDrafts,
        hasJourneysInProgress: Boolean(this.campaignActiveSubjectsCount),
      });

      this.modal = ModalStates.Archive;
    });
  }

  @action
  startUnarchive() {
    this.modal = ModalStates.Unarchive;
    this.analytics.track('campaign', 'unarchive_started', { model: this.args.model });
  }

  @action
  startRestart() {
    this.modal = ModalStates.Restart;
    this.analytics.track('campaign', 'restart_started', {
      model: this.args.model,
    });
  }

  @action
  startCopy() {
    let promise = this.onNewsletterCopy(this.args.model as NewsletterModel);

    promise.then(() => {
      if (this.args.afterCopyAction != null) {
        this.args.afterCopyAction();
      }
    });

    this.analytics.track('broadcast', 'copy_started', { model: this.args.model });
  }

  @action
  startCopyToEnv() {
    this.modal = ModalStates.CopyToEnv;
    this.analytics.track('broadcast', 'copy_to_workspace_started', { model: this.args.model });
  }

  private onNewsletterCopy(newsletter: NewsletterModel, env?: EnvData) {
    let promise = newsletter.copy({ envID: env?.id });
    let successMessage = isEmpty(env) ? 'Copy created' : `Copy created in ${env?.name}`;

    promise.then(() => {
      if (isEmpty(env) && this.args.afterCopyAction != null) {
        this.args.afterCopyAction();
      }
      this.alerts.success('broadcast.newsletter.copy.success', successMessage);
    }, ignorePromiseFailure);

    return promise;
  }

  get campaignRecipientNoun() {
    if (this.args.model instanceof CampaignModel) {
      return this.args.model.recipientNoun;
    }

    return {
      singular: 'person',
      plural: 'people',
    };
  }
}
